/**
 * Iter 47 · Central de Notificações — Sino no header
 * Popover com últimas 8 · check para marcar como lida · link "Ver todas".
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TIPO_ICON = {
  meta_alcancada: "🎉",
  lead_pendente: "🎯",
  lead_direcionado: "🎯",
  captacao_auto: "📥",
  captacao_resumo: "📊",
  manual: "📣",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "agora";
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState({ total: 0, nao_lidas: 0 });
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);
  const nav = useNavigate();

  const loadCount = useCallback(() => {
    api.get("/notificacoes/count").then((r) => setCount(r.data || { total: 0, nao_lidas: 0 })).catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get("/notificacoes?limit=8")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 45000);
    const onNew = () => loadCount();
    window.addEventListener("ws:notificacao_nova", onNew);
    return () => { clearInterval(t); window.removeEventListener("ws:notificacao_nova", onNew); };
  }, [loadCount]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const marcarLida = async (nid, e) => {
    e?.stopPropagation();
    try {
      await api.post(`/notificacoes/${nid}/lida`);
      setItems((prev) => prev.map((n) => (n.id === nid ? { ...n, lida: true } : n)));
      setCount((c) => ({ ...c, nao_lidas: Math.max(0, c.nao_lidas - 1) }));
    } catch { toast.error("Erro"); }
  };

  const marcarTodas = async () => {
    try {
      await api.post("/notificacoes/marcar-todas-lidas");
      setItems((prev) => prev.map((n) => ({ ...n, lida: true })));
      setCount((c) => ({ ...c, nao_lidas: 0 }));
      toast.success("Notificações marcadas como lidas");
    } catch { toast.error("Erro"); }
  };

  const abrirItem = (n) => {
    if (!n.lida) marcarLida(n.id);
    if (n.link_url) { nav(n.link_url); setOpen(false); }
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-slate-100"
        title="Notificações"
        data-testid="notif-bell"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {count.nao_lidas > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse"
            data-testid="notif-badge"
          >
            {count.nao_lidas > 99 ? "99+" : count.nao_lidas}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50"
          data-testid="notif-popover"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="font-display font-bold text-slate-900 flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notificações
              {count.nao_lidas > 0 && (
                <span className="text-[11px] bg-red-100 text-red-700 rounded px-1.5 py-0.5 font-bold">
                  {count.nao_lidas} nova{count.nao_lidas > 1 ? "s" : ""}
                </span>
              )}
            </div>
            {count.nao_lidas > 0 && (
              <button
                onClick={marcarTodas}
                className="text-xs text-blue-700 hover:underline font-bold flex items-center gap-1"
                data-testid="notif-marcar-todas"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {loading && items.length === 0 && (
              <div className="p-6 space-y-3" data-testid="notif-loading">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-6 h-6 rounded-full bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-4/5 bg-slate-200 rounded" />
                      <div className="h-2 w-full bg-slate-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="p-8 text-center text-slate-400 text-sm" data-testid="notif-vazio">
                Você não tem notificações ainda.
              </div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                onClick={() => abrirItem(n)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") abrirItem(n); }}
                className={`cursor-pointer w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 transition ${n.lida ? "" : "bg-blue-50/50"}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="text-xl leading-none pt-0.5">
                  {TIPO_ICON[n.tipo] || "🔔"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${n.lida ? "text-slate-700" : "font-bold text-slate-900"} truncate`}>
                    {n.titulo}
                  </div>
                  <div className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.corpo}</div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-slate-400">
                      {timeAgo(n.criado_em)}{n.criado_por_nome ? ` · ${n.criado_por_nome}` : ""}
                    </span>
                    {n.link_url && <ExternalLink className="w-3 h-3 text-slate-400" />}
                  </div>
                </div>
                {!n.lida && (
                  <button
                    onClick={(e) => marcarLida(n.id, e)}
                    className="text-slate-400 hover:text-emerald-600 self-start"
                    title="Marcar como lida"
                    data-testid={`notif-marcar-${n.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="p-2 border-t border-slate-100">
            <button
              onClick={() => { nav("/notificacoes"); setOpen(false); }}
              className="w-full text-center text-sm font-bold text-blue-700 hover:bg-blue-50 rounded-lg py-1.5"
              data-testid="notif-ver-todas"
            >
              Ver todas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
